import React, { useCallback } from "react";
import { TablePagination, TablePaginationProps } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { NJIM } from "../../../../theme/njimTheme";

const rtlTheme = createTheme({ direction: "rtl" });
const ltrTheme = createTheme({ direction: "ltr" });

export type AppTablePaginationProps = TablePaginationProps;

/**
 * Table pagination with correct RTL arrow direction and localized labels.
 * MUI swaps prev/next icons when theme.direction is "rtl".
 */
const AppTablePagination: React.FC<AppTablePaginationProps> = ({
  sx,
  labelRowsPerPage,
  labelDisplayedRows,
  ...rest
}) => {
  const { i18n, t } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");
  const theme = isRTL ? rtlTheme : ltrTheme;

  const defaultLabelDisplayedRows = useCallback(
    ({ from, to, count }: { from: number; to: number; count: number }) => {
      const total = count !== -1 ? count : to;
      return t("common.displayedRows", { from, to, total });
    },
    [t]
  );

  return (
    <ThemeProvider theme={theme}>
      <TablePagination
        dir={isRTL ? "rtl" : "ltr"}
        labelRowsPerPage={labelRowsPerPage ?? t("common.rowsPerPage")}
        labelDisplayedRows={labelDisplayedRows ?? defaultLabelDisplayedRows}
        sx={{
          color: NJIM.textSecondary,
          borderTop: `1px solid ${NJIM.border}`,
          ...sx,
        }}
        {...rest}
      />
    </ThemeProvider>
  );
};

export default AppTablePagination;
